import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Upload, Download, UserCircle2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { generateCustomerReportPDF } from '../utils/PDFGenerator';
import CrewFilter from '../components/CrewFilter';
import useCloudSync from '../hooks/useCloudSync';
import { IconTile, TINT } from '../components/ui/IconTile';
import { ActionTile } from '../components/ui/ActionTile';
import CustomerInvoicesModal from '../components/customers/CustomerInvoicesModal';
import {
  listCrews,
  getCrewName,
  getCrewColor,
  getActiveCrewFilter,
} from '../services/CrewService';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    // Split "person's name" out of the company name so an invoice can
    // read "ACME Cleaning · Roberto Parada" when the customer is a
    // business AND we still want the primary contact on file.
    contactFirstName: '',
    contactLastName: '',
    email: '',
    phone: '',
    phone2: '',
    address: '',
    notes: '',
    crewId: ''
  });
  // Crew lookup + filter state. Filter is synced via the global window event
  // dispatched by CrewFilter so multiple instances stay in lockstep.
  const [crews, setCrews] = useState(listCrews());
  const [crewFilter, setCrewFilter] = useState(getActiveCrewFilter());
  // Customer whose invoice history is currently open (null = closed).
  const [invoiceHistoryCustomer, setInvoiceHistoryCustomer] = useState(null);
  useEffect(() => {
    const onFilter = (e) => setCrewFilter(e.detail?.crewId || '');
    const onStorage = () => setCrews(listCrews());
    window.addEventListener('maidly:crew-filter-changed', onFilter);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('maidly:crew-filter-changed', onFilter);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    loadCustomers();
  }, []);

  // Realtime: refresh when another device pushes customer changes
  useCloudSync(() => {
    const data = JSON.parse(localStorage.getItem('maidly_customers') || '[]');
    setCustomers(data);
  }, ['customers']);

  const loadCustomers = () => {
    const data = JSON.parse(localStorage.getItem('maidly_customers') || '[]');
    setCustomers(data);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingCustomer) {
      const updated = customers.map(cust => 
        cust.id === editingCustomer.id ? { ...cust, ...formData } : cust
      );
      setCustomers(updated);
      localStorage.setItem('maidly_customers', JSON.stringify(updated));
      
      const invoices = JSON.parse(localStorage.getItem('maidly_invoices') || '[]');
      const updatedInvoices = invoices.map(inv => 
        inv.customerId === editingCustomer.id ? { ...inv, customerName: formData.name } : inv
      );
      localStorage.setItem('maidly_invoices', JSON.stringify(updatedInvoices));
      
      const quotes = JSON.parse(localStorage.getItem('maidly_quotes') || '[]');
      const updatedQuotes = quotes.map(q => 
        q.customerId === editingCustomer.id ? { ...q, customerName: formData.name } : q
      );
      localStorage.setItem('maidly_quotes', JSON.stringify(updatedQuotes));
      
      window.dispatchEvent(new CustomEvent('maidly:customers-updated', { detail: { action: 'edit', id: editingCustomer.id } }));
      toast.success('Customer updated successfully');
    } else {
      const newCustomer = {
        ...formData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString()
      };
      const updated = [...customers, newCustomer];
      setCustomers(updated);
      localStorage.setItem('maidly_customers', JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('maidly:customers-updated', { detail: { action: 'create', id: newCustomer.id } }));
      toast.success('Customer added successfully');
    }
    
    resetForm();
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      const updated = customers.filter(cust => cust.id !== id);
      setCustomers(updated);
      localStorage.setItem('maidly_customers', JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('maidly:customers-updated', { detail: { action: 'delete', id } }));
      toast.success('Customer deleted successfully');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      contactFirstName: customer.contactFirstName || '',
      contactLastName: customer.contactLastName || '',
      email: customer.email,
      phone: customer.phone,
      phone2: customer.phone2 || '',
      address: customer.address,
      notes: customer.notes,
      crewId: customer.crewId || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingCustomer(null);
    setFormData({
      name: '',
      contactFirstName: '',
      contactLastName: '',
      email: '',
      phone: '',
      phone2: '',
      address: '',
      notes: '',
      crewId: ''
    });
  };

  const exportToCSV = () => {
    if (customers.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Generate PDF report
    generateCustomerReportPDF(customers);
    toast.success('PDF Report downloaded!');
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          toast.error('CSV file is empty or invalid');
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const requiredHeaders = ['name', 'email', 'phone', 'address'];
        const hasValidHeaders = requiredHeaders.every(h => headers.includes(h));

        if (!hasValidHeaders) {
          toast.error('CSV must have headers: Name, Email, Phone, Address');
          return;
        }

        const newCustomers = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',');
          if (values.length >= 4) {
            newCustomers.push({
              id: Date.now().toString() + i,
              name: values[headers.indexOf('name')]?.trim() || '',
              email: values[headers.indexOf('email')]?.trim() || '',
              phone: values[headers.indexOf('phone')]?.trim() || '',
              address: values[headers.indexOf('address')]?.trim() || '',
              notes: values[headers.indexOf('notes')]?.trim() || '',
              createdAt: new Date().toISOString()
            });
          }
        }

        const updated = [...customers, ...newCustomers];
        setCustomers(updated);
        localStorage.setItem('maidly_customers', JSON.stringify(updated));
        toast.success(`Imported ${newCustomers.length} customers successfully`);
      } catch (error) {
        toast.error('Failed to import CSV');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div data-testid="customer-management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <IconTile icon={UserCircle2} tint={TINT.customers} size={36} />
          Customer Management
        </h1>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <CrewFilter />

          {/* Import CSV — orange (matches Scheduling tint) */}
          <label
            htmlFor="csv-import"
            data-testid="import-csv-label"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              cursor: 'pointer', margin: 0,
              fontSize: '0.9rem', fontWeight: 600, color: '#374151',
              transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#f9731609';
              e.currentTarget.style.borderColor = '#f9731666';
              e.currentTarget.style.color = '#f97316';
              e.currentTarget.style.boxShadow = '0 6px 16px #f9731633';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Upload} tint="#f97316" size={30} />
            Import CSV
          </label>
          <input
            id="csv-import"
            type="file"
            accept=".csv"
            onChange={handleImportCSV}
            data-testid="import-csv-input"
            style={{ display: 'none' }}
          />

          {/* Export CSV — blue (matches Time Clock tint) */}
          <button
            onClick={exportToCSV}
            data-testid="export-csv-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: 600, color: '#374151',
              transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#3b82f609';
              e.currentTarget.style.borderColor = '#3b82f666';
              e.currentTarget.style.color = '#3b82f6';
              e.currentTarget.style.boxShadow = '0 6px 16px #3b82f633';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Download} tint="#3b82f6" size={30} />
            Export CSV
          </button>

          {/* Add Customer — pink primary (matches Customers tint) */}
          <button
            onClick={() => setShowModal(true)}
            data-testid="add-customer-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem 0.55rem 0.55rem',
              background: TINT.customers, color: '#fff',
              border: 'none', borderRadius: 12,
              cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: 700,
              boxShadow: `0 6px 16px ${TINT.customers}55`,
              transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
              letterSpacing: 0.1,
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#db2777';
              e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.customers}88`;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = TINT.customers;
              e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.customers}55`;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Plus} tint="rgba(255,255,255,0.22)" size={30} />
            Add Customer
          </button>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Address</th>
              <th>Crew</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers
              .filter((c) => !crewFilter || c.crewId === crewFilter)
              .length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  {crewFilter ? 'No customers in this crew' : 'No customers found'}
                </td>
              </tr>
            ) : (
              customers
                .filter((c) => !crewFilter || c.crewId === crewFilter)
                .map(customer => (
                <tr key={customer.id} data-testid={`customer-row-${customer.id}`}>
                  <td>{customer.name}</td>
                  <td>{customer.email}</td>
                  <td>{customer.phone}</td>
                  <td>{customer.address}</td>
                  <td data-testid={`customer-crew-${customer.id}`}>
                    {customer.crewId ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          padding: '0.2rem 0.6rem',
                          borderRadius: 999,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: `${getCrewColor(customer.crewId)}1a`,
                          color: getCrewColor(customer.crewId),
                          border: `1px solid ${getCrewColor(customer.crewId)}40`,
                        }}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: getCrewColor(customer.crewId) }} />
                        {getCrewName(customer.crewId) || 'Crew'}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <ActionTile
                        onClick={() => setInvoiceHistoryCustomer(customer)}
                        testid={`view-invoices-${customer.id}`}
                        title="View invoices & history"
                        icon={Receipt}
                        tint={TINT.invoices}
                      />
                      <ActionTile
                        onClick={() => handleEdit(customer)}
                        testid={`edit-customer-${customer.id}`}
                        title="Edit customer"
                        icon={Edit}
                        tint={TINT.timeClock}
                      />
                      <ActionTile
                        onClick={() => handleDelete(customer.id)}
                        testid={`delete-customer-${customer.id}`}
                        title="Delete customer"
                        icon={Trash2}
                        tint={TINT.expenses}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                {editingCustomer ? 'Edit Customer' : 'Add Customer'}
              </h2>
              <button onClick={resetForm} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
              {/* Customer (business or person name) */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.35rem' }}>Customer *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="customer-name-input"
                  required
                  className="input"
                />
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.35rem' }}>
                  Name of a business or person.
                </div>
              </div>

              {/* Primary contact block */}
              <div
                data-testid="primary-contact-card"
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  padding: '1rem',
                  background: '#fafafa',
                  marginBottom: '1rem',
                }}
              >
                <div style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.75rem', color: '#0f172a' }}>
                  Primary contact
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.35rem', color: '#475569' }}>First name</label>
                    <input
                      type="text"
                      value={formData.contactFirstName}
                      onChange={(e) => setFormData({ ...formData, contactFirstName: e.target.value })}
                      data-testid="customer-contact-first-name-input"
                      className="input"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.35rem', color: '#475569' }}>Last name</label>
                    <input
                      type="text"
                      value={formData.contactLastName}
                      onChange={(e) => setFormData({ ...formData, contactLastName: e.target.value })}
                      data-testid="customer-contact-last-name-input"
                      className="input"
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.35rem', color: '#475569' }}>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="customer-email-input"
                    className="input"
                  />
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.35rem', color: '#475569' }}>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                  />
                </div>
                {formData.phone2 !== undefined && formData.phone2 !== null && formData.phone2 !== '' ? (
                  <div style={{ marginBottom: '0.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.35rem', color: '#475569' }}>Second phone</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="tel"
                        value={formData.phone2}
                        onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                        className="input"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, phone2: '' })}
                        style={{
                          background: 'transparent', border: '1px solid #e5e7eb',
                          borderRadius: 6, padding: '0 10px', cursor: 'pointer', color: '#64748b',
                        }}
                        title="Remove second phone"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="customer-add-phone-btn"
                    onClick={() => setFormData({ ...formData, phone2: ' ' })}
                    style={{
                      background: 'transparent', border: 'none', color: '#0073ea',
                      fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: 0,
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    <Plus size={12} /> Add phone
                  </button>
                )}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input"
                  style={{ minHeight: '80px' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  style={{ minHeight: '80px' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Crew</label>
                <select
                  value={formData.crewId}
                  onChange={(e) => setFormData({ ...formData, crewId: e.target.value })}
                  data-testid="customer-crew-select"
                  className="input"
                >
                  <option value="">— No crew —</option>
                  {crews.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {crews.length === 0 && (
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.4rem' }}>
                    Create crews in the Crews page to assign customers to a sub-team.
                  </p>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" data-testid="save-customer-button" className="btn btn-primary">
                  {editingCustomer ? 'Update' : 'Add'} Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {invoiceHistoryCustomer && (
        <CustomerInvoicesModal
          customer={invoiceHistoryCustomer}
          onClose={() => setInvoiceHistoryCustomer(null)}
        />
      )}
    </div>
  );
};

export default CustomerManagement;
